<template>
    <a-popup :visible="visible" @cancel="onCancel">
        <div class="tw-mx-auto tw-w-[95%] md:tw-max-w-[480px] tw-bg-white tw-rounded-2xl tw-shadow-xl tw-overflow-hidden">
            <!-- Creative Header with User Avatars -->
            <div class="tw-relative tw-bg-gradient-to-br tw-from-primary-50 tw-to-primary-100/50 tw-px-6 tw-pt-12 tw-pb-20">
                <div class="tw-absolute tw-top-4 tw-right-4">
                    <button @click="onCancel" class="tw-p-2 tw-rounded-full hover:tw-bg-white/30 tw-transition-colors">
                        <icon icon="solar:close-circle-bold" class="tw-text-xl tw-text-gray-600" />
                    </button>
                </div>
                
                <!-- Connection Animation with User Avatars -->
                <div class="tw-flex tw-items-center tw-justify-center tw-gap-4">
                    <!-- Current User Avatar -->
                    <div class="tw-flex tw-flex-col tw-items-center">
                        <div class="tw-w-16 tw-h-16 tw-rounded-full tw-bg-white tw-shadow-md tw-flex tw-items-center tw-justify-center tw-overflow-hidden tw-border-4 tw-border-white">
                            <img 
                                v-if="currentUser?.profile_image" 
                                :src="currentUser.profile_image" 
                                :alt="currentUser?.name"
                                class="tw-w-full tw-h-full tw-object-cover"
                            />
                            <icon v-else icon="solar:user-bold" class="tw-text-3xl tw-text-gray-400" />
                        </div>
                        <span class="tw-mt-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-text-center">
                            {{ currentUser?.name || $t('common.you') }}
                        </span>
                    </div>

                    <!-- Connection Animation -->
                    <div class="tw-flex tw-items-center tw-gap-1">
                        <div class="tw-w-2 tw-h-2 tw-rounded-full tw-bg-primary-300 tw-animate-pulse"></div>
                        <div class="tw-w-2 tw-h-2 tw-rounded-full tw-bg-primary-300 tw-animate-pulse" style="animation-delay: 0.15s"></div>
                        <div class="tw-w-2 tw-h-2 tw-rounded-full tw-bg-primary-400 tw-animate-pulse" style="animation-delay: 0.3s"></div>
                        <div class="tw-w-2 tw-h-2 tw-rounded-full tw-bg-primary-400 tw-animate-pulse" style="animation-delay: 0.45s"></div>
                        <div class="tw-w-2 tw-h-2 tw-rounded-full tw-bg-primary-500 tw-animate-pulse" style="animation-delay: 0.6s"></div>
                    </div>

                    <!-- Target User Avatar -->
                    <div class="tw-flex tw-flex-col tw-items-center">
                        <div class="tw-w-16 tw-h-16 tw-rounded-full tw-bg-white tw-shadow-md tw-flex tw-items-center tw-justify-center tw-overflow-hidden tw-border-4 tw-border-white">
                            <img 
                                v-if="targetUser?.profile_image" 
                                :src="targetUser.profile_image" 
                                :alt="targetUser?.name"
                                class="tw-w-full tw-h-full tw-object-cover"
                            />
                            <icon v-else icon="solar:user-bold" class="tw-text-3xl tw-text-gray-400" />
                        </div>
                        <span class="tw-mt-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-text-center">
                            {{ targetUser?.name || $t('common.user') }}
                        </span>
                    </div>
                </div>
            </div>

            <!-- Message Card - Overlapping the header -->
            <div class="tw-relative tw-px-6 -tw-mt-8 tw-mb-6">
                <div class="tw-bg-white tw-rounded-xl tw-shadow-sm tw-p-5 tw-border tw-border-gray-100">
                    <h2 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-1">
                        {{ $t('common.send-connection-request') }}
                    </h2>
                    <p class="tw-text-sm tw-text-gray-600 tw-mb-4">
                        {{ $t('messages.write-message-to-connect-to', { name: targetUser?.name || $t('common.user') }) }}
                    </p>
                    
                    <div class="tw-relative">
                        <FormField
                            v-model="message"
                            type="textarea"
                            :placeholder="$t('forms.write-your-message')"
                            :error="messageError"
                            rows="3"
                            class="!tw-mb-0"
                        />
                        <div class="tw-absolute tw-bottom-2 tw-right-2">
                            <span class="tw-text-xs tw-font-medium tw-px-2 tw-py-1 tw-rounded-full"
                                :class="characterCount > 450 ? 'tw-bg-rose-50 tw-text-rose-500' : 'tw-bg-gray-50 tw-text-gray-400'">
                                {{ characterCount }}/500
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Footer -->
            <div class="tw-px-6 tw-pb-6 tw-flex tw-items-center tw-justify-end tw-gap-3">
                <a-button 
                    intent="white" 
                    :disabled="isLoading" 
                    @click="onCancel"
                    class="tw-min-w-[100px]"
                >
                    {{ $t('buttons.cancel') }}
                </a-button>
                <a-button 
                    intent="primary" 
                    :disabled="isLoading || !isValid" 
                    :loading="isLoading" 
                    @click="handleSubmit"
                    class="tw-min-w-[100px]"
                >
                    {{ $t('buttons.send') }}
                </a-button>
            </div>
        </div>
    </a-popup>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores/auth';
import FormField from '@/components/form/FormField.vue';
import useAlert from '@/composables/useAlert';
import { defaultConnectionMessage } from '@/config/params';
import { sendRequest } from '@/api/connections';

const { t } = useI18n();
const authStore = useAuthStore();

const props = defineProps({
    visible: {
        type: Boolean,
        default: false
    },
    targetUser: {
        required: true
    }
});

const emit = defineEmits(['close', 'sent']);

// User data
const currentUser = computed(() => authStore.user);

// Form state
const message = ref(defaultConnectionMessage);
const isLoading = ref(false);
const messageError = ref('');

// Computed properties
const characterCount = computed(() => message.value.length);
const isValid = computed(() => {
    const messageLength = message.value.trim().length;
    return messageLength > 0 && messageLength <= 500;
});

// Methods
const validateMessage = () => {
    messageError.value = '';
    const messageLength = message.value.trim().length;
    
    if (messageLength === 0) {
        messageError.value = t('errors.field-required');
        return false;
    }
    
    if (messageLength > 500) {
        messageError.value = t('errors.max-length', { max: 500 });
        return false;
    }
    
    return true;
};

const handleSubmit = async () => {
    if (!validateMessage()) return;
    
    isLoading.value = true;
    try {
        await sendRequest(props.targetUser.id, message.value.trim());
        useAlert(t('messages.connection-request-sent'));
        emit('sent');
        onCancel();
    } catch (error) {
        if (error.response?.status === 422 && error.response?.data?.errors?.receiver_id?.includes('Connection request already exists')) {
            useAlert(t('errors.connection-request-already-exists', 'Connection request already exists'), 'warning');
        } else {
            useAlert(t('errors.something-went-wrong'), 'error');
        }
    } finally {
        isLoading.value = false;
    }
};

const onCancel = () => {
    message.value = defaultConnectionMessage;
    messageError.value = '';
    emit('close');
};
</script> 